import { useEffect, useState, type FormEvent } from 'react';
import type { Vehicle, VehicleDraft } from '@/types/Vehicle';
import { newVehicleId } from '@/lib/vehicles';

interface VehicleFormProps {
  initial?: Vehicle | null;
  onSubmit: (draft: VehicleDraft) => Promise<void> | void;
  onCancel: () => void;
  busy?: boolean;
}

interface FormState {
  year: string;
  make: string;
  model: string;
  engine: string;
  vin: string;
  project: string;
  notes: string;
}

function vehicleToState(v: Vehicle | null | undefined): FormState {
  return {
    year: v ? String(v.year) : String(new Date().getFullYear() - 56),
    make: v?.make ?? 'Plymouth',
    model: v?.model ?? '',
    engine: v?.engine ?? '',
    vin: v?.vin ?? '',
    project: v?.project ?? '',
    notes: v?.notes ?? '',
  };
}

export default function VehicleForm({ initial, onSubmit, onCancel, busy }: VehicleFormProps) {
  const [form, setForm] = useState<FormState>(() => vehicleToState(initial));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(vehicleToState(initial));
  }, [initial]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const year = parseInt(form.year, 10);
    if (Number.isNaN(year) || year < 1900 || year > 2100) {
      setError('Year must be 1900-2100.');
      return;
    }
    if (!form.make.trim() || !form.model.trim()) {
      setError('Make and Model are required.');
      return;
    }
    const draft: VehicleDraft = {
      id: initial?.id ?? newVehicleId(),
      vin: form.vin.trim().toUpperCase(),
      year,
      make: form.make.trim(),
      model: form.model.trim(),
      engine: form.engine.trim(),
      ...(form.project.trim() ? { project: form.project.trim() } : {}),
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      wishlist: initial?.wishlist ?? [],
    };
    await onSubmit(draft);
  }

  return (
    <form onSubmit={handleSubmit} className="auth-form" style={{ maxWidth: 720 }}>
      <div className="form-row">
        <label className="auth-field">
          <span className="auth-label">Year</span>
          <input
            type="number"
            min="1900"
            max="2100"
            value={form.year}
            onChange={(e) => update('year', e.target.value)}
            required
            disabled={busy}
          />
        </label>
        <label className="auth-field">
          <span className="auth-label">Make</span>
          <input
            value={form.make}
            onChange={(e) => update('make', e.target.value)}
            placeholder="Plymouth, Dodge, etc."
            required
            disabled={busy}
          />
        </label>
        <label className="auth-field">
          <span className="auth-label">Model</span>
          <input
            value={form.model}
            onChange={(e) => update('model', e.target.value)}
            placeholder="Cuda, Charger Daytona, GTX"
            required
            disabled={busy}
          />
        </label>
      </div>
      <div className="form-row">
        <label className="auth-field">
          <span className="auth-label">Engine</span>
          <input
            value={form.engine}
            onChange={(e) => update('engine', e.target.value)}
            placeholder="426 Hemi 425hp"
            disabled={busy}
          />
        </label>
        <label className="auth-field">
          <span className="auth-label">VIN (optional)</span>
          <input
            value={form.vin}
            onChange={(e) => update('vin', e.target.value)}
            placeholder="JS23R0B123456"
            style={{ fontFamily: 'var(--mono)', letterSpacing: '0.08em' }}
            disabled={busy}
          />
        </label>
      </div>
      <label className="auth-field">
        <span className="auth-label">Project Name</span>
        <input
          value={form.project}
          onChange={(e) => update('project', e.target.value)}
          placeholder="Build #14 — Anderson Restorations"
          disabled={busy}
        />
      </label>
      <label className="auth-field">
        <span className="auth-label">Notes</span>
        <textarea
          rows={3}
          value={form.notes}
          onChange={(e) => update('notes', e.target.value)}
          placeholder="Any project notes, scope, deadlines, etc."
          disabled={busy}
          style={{ resize: 'vertical', fontFamily: 'var(--body)' }}
        />
      </label>
      {error && <div className="auth-error">{error}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button type="button" className="btn dark" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Saving…' : initial ? 'Save Changes' : 'Add Vehicle →'}
        </button>
      </div>
    </form>
  );
}
